/**
 * Company Distribution Chart Component
 * Shows which companies are hiring in a location and how many jobs they have
 * Uses a horizontal bar chart with company names and job counts
 */

import React, { memo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { safeParseNumber } from '../utils/numberUtils';

interface CompanyDistributionChartProps {
  companyDistribution: Record<string, number>; // companyId -> job count
  location: string;
  brandColour?: string;
  locale?: string;
  className?: string;
  maxItems?: number;
}

export const CompanyDistributionChart: React.FC<CompanyDistributionChartProps> = memo(({
  companyDistribution,
  location,
  brandColour,
  locale = 'en',
  className = '',
  maxItems = 15,
}) => {
  const intl = useIntl();
  
  if (!companyDistribution || Object.keys(companyDistribution).length === 0) {
    return null;
  }

  // Filter and validate data
  const validEntries = Object.entries(companyDistribution)
    .map(([label, value]) => [label, safeParseNumber(value)] as [string, number | null])
    .filter(([, value]) => value !== null && value > 0) as Array<[string, number]>;

  if (validEntries.length === 0) {
    return null;
  }

  // Sort by value descending and take top items
  const sortedEntries = validEntries
    .sort(([, a], [, b]) => b - a)
    .slice(0, maxItems);

  const maxValue = Math.max(...sortedEntries.map(([, value]) => value));
  const totalValue = sortedEntries.reduce((sum, [, value]) => sum + value, 0);

  // Chart dimensions
  const width = 600;
  const height = Math.min(400, sortedEntries.length * 35 + 80); // Dynamic height based on items
  const padding = { top: 40, right: 100, bottom: 40, left: 200 };
  const barHeight = 28;
  const barSpacing = 7;
  const chartWidth = width - padding.left - padding.right;
  const chartHeight = sortedEntries.length * (barHeight + barSpacing);

  // Color palette
  const primaryColor = brandColour || '#3b82f6';
  const baseColors = ['#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16', '#f97316', '#6366f1', '#14b8a6', '#f43f5e'];
  const colors = [primaryColor, ...baseColors];

  // Format company name for display (capitalize first letter of each word)
  const formatCompanyName = (companyId: string): string => {
    return companyId
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  return (
    <div className={`company-distribution-chart ${className}`}>
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
          <FormattedMessage 
            id="jobDetailPage.companiesHiringInLocation" 
            defaultMessage="Companies Hiring in {location}"
            description="Chart title showing companies that post jobs in a specific location, where {location} is the city/region"
            values={{ location }}
          />
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          <FormattedMessage 
            id="jobDetailPage.companyDistributionDescription" 
            defaultMessage="Showing top {count} companies by number of job postings"
            description="Description text explaining the company distribution chart shows top companies, where {count} is the number displayed"
            values={{ count: sortedEntries.length }}
          />
        </p>
      </div>
      
      <svg width={width} height={height} className="overflow-visible">
        <defs>
          {/* Gradient for bars */}
          <linearGradient id="companyBarGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor={primaryColor} stopOpacity="0.9" />
            <stop offset="100%" stopColor={primaryColor} stopOpacity="0.7" />
          </linearGradient>
        </defs>

        {/* Bars */}
        <g transform={`translate(${padding.left}, ${padding.top})`}>
          {sortedEntries.map(([companyId, value], index) => {
            const barWidth = (value / maxValue) * chartWidth;
            const y = index * (barHeight + barSpacing);
            const color = colors[index % colors.length];
            const percentage = ((value / totalValue) * 100).toFixed(1);

            return (
              <g key={companyId}>
                {/* Bar */}
                <rect
                  x={0}
                  y={y}
                  width={barWidth}
                  height={barHeight}
                  fill={color}
                  rx={4}
                  className="transition-opacity duration-300 hover:opacity-80"
                />
                
                {/* Company name (left of bar) */}
                <text
                  x={-10}
                  y={y + barHeight / 2}
                  textAnchor="end"
                  dominantBaseline="middle"
                  className="text-sm font-medium fill-gray-700 dark:fill-gray-200"
                  fontSize="14"
                >
                  {formatCompanyName(companyId)}
                </text>

                {/* Value label (right of bar) */}
                <text
                  x={barWidth + 10}
                  y={y + barHeight / 2}
                  textAnchor="start"
                  dominantBaseline="middle"
                  className="text-sm font-semibold fill-gray-900 dark:fill-gray-100"
                  fontSize="14"
                >
                  {value} {value === 1 ? 'job' : 'jobs'} ({percentage}%)
                </text>
              </g>
            );
          })}
        </g>

        {/* X-axis (if needed) */}
        <g transform={`translate(${padding.left}, ${padding.top + chartHeight})`}>
          <line
            x1={0}
            y1={0}
            x2={chartWidth}
            y2={0}
            stroke="currentColor"
            strokeWidth="1"
            className="text-gray-200 dark:text-gray-600"
          />
        </g>
      </svg>
    </div>
  );
});

CompanyDistributionChart.displayName = 'CompanyDistributionChart';


